import React from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Paper, Typography, Button } from '@mui/material';

import {
  AcneImage,
  SleepImage,
  AgeingImage,
  HerpesImage,
  NicotineImage,
  HairLossImage,
  AllergiesImage,
  ColdSoresImage,
  WeightLossImage,
  ContraceptivePillImage,
  MedicinalCannabisImage,
  ErectileDysfunctionImage,
  CheckUpImage,
} from '../../media/treatments';
import { getHealthCategoryDisplayName } from '../../utils';

const PREFIX = 'TreatmentThumbnails';

const classes = {
  section: `${PREFIX}-section`,
  treatmentImage: `${PREFIX}-treatmentImage`,
  paper: `${PREFIX}-paper`,
  selected: `${PREFIX}-selected`,
  button: `${PREFIX}-button`,
  heading: `${PREFIX}-heading`,
  divider: `${PREFIX}-divider`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.section}`]: {
    marginBottom: 50,
  },

  [`& .${classes.treatmentImage}`]: {
    width: '100%',
    margin: 'auto',
    padding: '10px 0px',
  },

  [`& .${classes.paper}`]: {
    padding: '10px',
    margin: '10px',
    '@media (max-width: 600px)': {
      padding: '5px',
      margin: '5px',
    },
  },

  [`& .${classes.selected}`]: {
    borderWidth: '3px',
    borderColor: '#2AAFBB',
    padding: '10px',
    margin: '10px',
    '@media (max-width: 600px)': {
      padding: '5px',
      margin: '5px',
    },
  },

  [`& .${classes.button}`]: {
    textDecoration: 'none',
    cursor: 'pointer',
    textTransform: 'none',
  },

  [`& .${classes.heading}`]: {
    fontWeight: 'bold',
  },

  [`& .${classes.divider}`]: {
    marginBottom: '30px',
  },
}));

const IMAGE_MAPPING = {
  acne: AcneImage,
  ageing: AgeingImage,
  allergies: AllergiesImage,
  checkup: CheckUpImage,
  'cold sores': ColdSoresImage,
  'contraceptive pill': ContraceptivePillImage,
  'erectile dysfunction': ErectileDysfunctionImage,
  'hair loss': HairLossImage,
  herpes: HerpesImage,
  'medicinal cannabis': MedicinalCannabisImage,
  sleep: SleepImage,
  skincare: AgeingImage, // same image file as ageing
  nicotine: NicotineImage,
  'weight loss': WeightLossImage,
};

const TREATMENT_MAPPING = {
  Ageing: 'Anti-Ageing',
  // Add more mappings here as needed
};

// remove duplicates and sort the treatments array
const getCleanedAndSortedTreatments = (treatments) => [...new Set(treatments || [])].sort();

const getAlternativeText = (treatment, treatmentMappings = TREATMENT_MAPPING) => {
  const mappedTreatment = treatmentMappings[treatment] || treatment;
  return `${mappedTreatment} Treatment`;
};

/**
 *
 * @param {object} props
 * @param {string} props.treatment - Name of the treatment to display
 * @param {(formRef: string) => void } props.handleThumbClick - Function to handle thumbnail click events
 * @param {string} props.selected - Currently selected treatment name
 * @param {boolean} [props.locked] - When true, render as greyed-out / disabled (use props.onLockedClick instead of handleThumbClick)
 * @param {(formRef: string) => void} [props.onLockedClick] - Click handler used when `locked` is true
 */
const Treatment = ({ treatment, handleThumbClick, selected, locked = false, onLockedClick }) => {
  if (!treatment) {
    return null;
  }

  const header = getHealthCategoryDisplayName(treatment);
  const selectedImage = IMAGE_MAPPING[treatment];
  const alternativeText = getAlternativeText(header);

  const onClickHandler = () => {
    if (locked) {
      onLockedClick?.(treatment);
      return;
    }
    handleThumbClick(treatment);
  };

  return (
    <StyledGrid size={{ xs: 6, sm: 4 }}>
      <Button onClick={onClickHandler} className={classes.button} sx={locked ? { opacity: 0.45 } : undefined}>
        <Paper className={selected === treatment ? classes.selected : classes.paper}>
          <Typography variant="body1" className={classes.heading} gutterBottom>
            {header}
          </Typography>
          <img src={selectedImage} className={classes.treatmentImage} alt={alternativeText} />
        </Paper>
      </Button>
    </StyledGrid>
  );
};

/**
 *
 * @param {object} props
 * @param {string[]} props.thumbnailsToDisplay - Array of treatment names to display as thumbnails
 * @param {string} props.heading - Heading text to display above the thumbnails
 * @param {(formRef: string) => void } props.handleThumbClick - Function to handle thumbnail click events
 * @param {string} [props.selected] - Currently selected treatment name
 * @param {string[]} [props.lockedTreatments] - Treatment names to render as greyed-out / disabled
 * @param {(formRef: string) => void} [props.onLockedClick] - Click handler used when a locked treatment is clicked
 */
const TreatmentThumbnails = ({
  thumbnailsToDisplay,
  heading,
  handleThumbClick,
  selected,
  lockedTreatments,
  onLockedClick,
}) => {
  if (!Array.isArray(thumbnailsToDisplay) || !thumbnailsToDisplay?.length) {
    return null;
  }

  const cleanedAndSortedTreatments = getCleanedAndSortedTreatments(thumbnailsToDisplay);
  const lockedSet = new Set(lockedTreatments || []);

  return (
    <StyledGrid container className={classes.section}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h5" sx={{ textAlign: 'center' }} gutterBottom>
          {heading}
        </Typography>
        <br />
      </Grid>
      {cleanedAndSortedTreatments.map((treatment) => (
        <Treatment
          treatment={treatment}
          handleThumbClick={handleThumbClick}
          selected={selected || ''}
          key={treatment}
          locked={lockedSet.has(treatment)}
          onLockedClick={onLockedClick}
        />
      ))}
    </StyledGrid>
  );
};

export default TreatmentThumbnails;
